import { type AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';
import { useAuth } from '../hook_au/useAuth';
import {
  type SubprojectDetail,
  type SubprojectListItem,
  type Room,
  type RoomItem,
  type RoomItemCreatePayload,
  type RoomItemCreateFromCatalogPayload,
  type RoomItemDuplicatePayload,
  type RoomUpdatePayload,
  type RoomItemUpdatePayload,
  type RoomCreatePayload,
  type SubprojectExtra,
  type SubprojectExtraCreatePayload,
  type SubprojectExtraUpdatePayload,
  type SubprojectPayload,
  type SubprojectPdfOptionalColumn,
  type Uuid,
} from '../types/subprojects';
import apiClient from './apiClient';

type RequestConfig<Response> = AxiosRequestConfig & { skipAuth?: boolean };

export function useSubprojectsApi() {
  const { token } = useAuth();

  const request = useCallback(
    async <Response>(config: RequestConfig<Response>): Promise<Response> => {
      if (!token && !config.skipAuth) {
        throw new Error('No hay sesión activa para llamar a la API de subproyectos');
      }

      const response = await apiClient.request<Response>({
        ...config,
        url: `/subprojects${config.url ?? ''}`,
      });

      return response.data;
    },
    [token],
  );

  const listSubprojects = useCallback(
    (projectId: Uuid, pagination?: { limit?: number; offset?: number }) =>
      request<SubprojectListItem[]>({
        method: 'GET',
        url: '/list',
        params: { project_id: projectId, limit: pagination?.limit, offset: pagination?.offset },
      }),
    [request],
  );

  const getSubproject = useCallback(
    (subprojectId: Uuid) =>
      request<SubprojectDetail>({
        method: 'GET',
        url: `/${subprojectId}`,
      }),
    [request],
  );

  const createSubproject = useCallback(
    (payload: SubprojectPayload) =>
      request<SubprojectDetail>({
        method: 'POST',
        url: '/create',
        data: payload,
      }),
    [request],
  );

  const updateSubproject = useCallback(
    (subprojectId: Uuid, payload: Partial<SubprojectPayload>) =>
      request<SubprojectDetail>({
        method: 'PUT',
        url: `/${subprojectId}`,
        data: payload,
      }),
    [request],
  );

  const deleteSubproject = useCallback(
    (subprojectId: Uuid) =>
      request<void>({
        method: 'DELETE',
        url: `/${subprojectId}`,
      }),
    [request],
  );

  const listRoomItems = useCallback(
    (subprojectId: Uuid, roomId: Uuid) =>
      request<RoomItem[]>({
        method: 'GET',
        url: `/${subprojectId}/rooms/${roomId}/items`,
      }),
    [request],
  );

  const createRoomItem = useCallback(
    (subprojectId: Uuid, roomId: Uuid, payload: RoomItemCreatePayload) =>
      request<RoomItem>({
        method: 'POST',
        url: `/${subprojectId}/rooms/${roomId}/items`,
        data: payload,
      }),
    [request],
  );

  const createRoomItemFromCatalog = useCallback(
    (subprojectId: Uuid, roomId: Uuid, payload: RoomItemCreateFromCatalogPayload) =>
      request<RoomItem>({
        method: 'POST',
        url: `/${subprojectId}/rooms/${roomId}/items/create-from-catalog`,
        data: payload,
      }),
    [request],
  );

  const createRoom = useCallback(
  (subprojectId: Uuid, payload: RoomCreatePayload) =>
    request<Room>({
      method: 'POST',
      url: `/${subprojectId}/rooms`,
      data: payload,
    }),
  [request],
  );

  const listSubprojectExtras = useCallback(
    (subprojectId: Uuid) =>
      request<SubprojectExtra[]>({
        method: 'GET',
        url: `/${subprojectId}/extras`,
      }),
    [request],
  );

  const createSubprojectExtra = useCallback(
    (subprojectId: Uuid, payload: SubprojectExtraCreatePayload) =>
      request<SubprojectExtra>({
        method: 'POST',
        url: `/${subprojectId}/extras`,
        data: payload,
      }),
    [request],
  );

  const updateSubprojectExtra = useCallback(
    (subprojectId: Uuid, extraId: Uuid, payload: SubprojectExtraUpdatePayload) =>
      request<SubprojectExtra>({
        method: 'PUT',
        url: `/${subprojectId}/extras/${extraId}`,
        data: payload,
      }),
    [request],
  );

  const deleteSubprojectExtra = useCallback(
    (subprojectId: Uuid, extraId: Uuid) =>
      request<void>({
        method: 'DELETE',
        url: `/${subprojectId}/extras/${extraId}`,
      }),
    [request],
  );

  const updateRoom = useCallback(
  (subprojectId: Uuid, roomId: Uuid, payload: RoomUpdatePayload) =>
    request<Room>({
      method: 'PUT',
      url: `/${subprojectId}/rooms/${roomId}`,
      data: payload,
    }),
  [request],
);

  const updateRoomItem = useCallback(
    (subprojectId: Uuid, roomId: Uuid, itemId: Uuid, payload: RoomItemUpdatePayload) =>
      request<RoomItem>({
        method: 'PUT',
        url: `/${subprojectId}/rooms/${roomId}/items/${itemId}`,
        data: payload,
      }),
    [request],
  );

  const deleteRoomItem = useCallback(
    (subprojectId: Uuid, roomId: Uuid, itemId: Uuid) =>
      request<void>({
        method: 'DELETE',
        url: `/${subprojectId}/rooms/${roomId}/items/${itemId}`,
      }),
    [request],
  );

  const duplicateRoomItem = useCallback(
    (
      subprojectId: Uuid,
      roomId: Uuid,
      itemId: Uuid,
      payload: RoomItemDuplicatePayload,
    ) =>
      request<RoomItem>({
        method: 'POST',
        url: `/${subprojectId}/rooms/${roomId}/items/${itemId}/duplicate`,
        data: payload,
      }),
    [request],
  );

  const downloadSubprojectPdf = useCallback(
    (
      subprojectId: Uuid,
      options?: { optionalColumns?: SubprojectPdfOptionalColumn[] },
    ) => {
      const query = new URLSearchParams();
      if (options?.optionalColumns) {
        if (options.optionalColumns.length === 0) {
          // Empty marker so backend can distinguish "no optional columns"
          // from "no query provided" (which uses defaults).
          query.append('optional_columns', '');
        } else {
          options.optionalColumns.forEach((column) => query.append('optional_columns', column));
        }
      }
      const queryString = query.toString();

      return request<Blob>({
        method: 'GET',
        url: `/${subprojectId}/pdf${queryString ? `?${queryString}` : ''}`,
        responseType: 'blob',
      });
    },
    [request],
  );

  const downloadSubprojectExcel = useCallback(
    (subprojectId: Uuid) =>
      request<Blob>({
        method: 'GET',
        url: `/${subprojectId}/excel`,
        responseType: 'blob',
      }),
    [request],
  );

  return {
    listSubprojects,
    getSubproject,
    createSubproject,
    updateSubproject,
    deleteSubproject,
    listRoomItems,
    listSubprojectExtras,
    createSubprojectExtra,
    updateSubprojectExtra,
    deleteSubprojectExtra,
    createRoom,
    updateRoom,
    createRoomItem,
    createRoomItemFromCatalog,
    updateRoomItem,
    deleteRoomItem,
    duplicateRoomItem,
    downloadSubprojectPdf,
    downloadSubprojectExcel,
  };
}
